import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import Paper from "@material-ui/core/Paper";
import LinearProgress from "@material-ui/core/LinearProgress";
import StudentFamilyBranchRow from "./StudentFamilyBranchRow";
import AddNewBranchModal from "./AddNewBranchModal";
import { useStudentFamilyBranch } from "./StudentFamilyBranchContext";
import { Alert } from "react-bootstrap";
import { useSelector, shallowEqual } from "react-redux";

// --- Table Head ---

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headRows = [
  {
    id: "user_id",
    numeric: false,
    disablePadding: false,
    label: "Student ID",
    sortable: true,
  },
  {
    id: "student_name",
    numeric: false,
    disablePadding: false,
    label: "Student Name",
    sortable: true,
  },
  {
    id: "parent_name",
    numeric: false,
    disablePadding: false,
    label: "Primary Parent Name",
    sortable: true,
  },
  {
    id: "contact_info",
    numeric: false,
    disablePadding: false,
    label: "Contact Information",
    sortable: false,
  },
  {
    id: "branches",
    numeric: false,
    disablePadding: false,
    label: "Locations",
    sortable: false,
  },
  {
    id: "actions",
    numeric: false,
    disablePadding: false,
    label: "Actions",
    sortable: false,
  },
];

function StudentFamilyBranchTableHead(props) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headRows.map((row) => (
          <TableCell
            key={row.id}
            align={row.numeric ? "right" : "left"}
            padding={row.disablePadding ? "none" : "default"}
            sortDirection={orderBy === row.id ? order : false}
          >
            {row.sortable ? (
              <TableSortLabel
                active={orderBy === row.id}
                direction={orderBy === row.id ? order : "asc"}
                onClick={createSortHandler(row.id)}
              >
                {row.label}
              </TableSortLabel>
            ) : (
              row.label
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

StudentFamilyBranchTableHead.propTypes = {};

// --- Styles ---
const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    marginTop: theme.spacing(3),
  },
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 750,
  },
  tableWrapper: {
    overflowX: "auto",
  },
}));

// --- Main Table Component ---
export default function StudentFamilyBranchTable() {
  const classes = useStyles();
  const { studentSearchResults, loading, reSearchStudents } =
    useStudentFamilyBranch();
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("student_name");
  const [successMessage, setSuccessMessage] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [showAddNewBranchModal, setShowAddNewBranchModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const currentBranch = useSelector(
    (state) => state.auth.branches.currentBranch,
    shallowEqual
  );
  const rows = (studentSearchResults.students || []).map((student) => ({
    ...student,
    student_name: `${student.firstname} ${student.lastname}`,
    parent_name: `${student.parent_firstname || ""} ${student.parent_lastname ||
      ""}`.trim(),
  }));

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleBranchAdded = () => {
    setSuccessMessage(
      `Successfully added ${selectedStudent.firstname} ${selectedStudent.lastname} (ID: ${selectedStudent.user_id}) and family members to the ${currentBranch.branch_name} location!`
    );

    reSearchStudents();

    setTimeout(() => {
      setSuccessMessage(null);
    }, 5000);
  };

  const handleAddLocationClick = (student) => {
    setSelectedStudent(student);
    setShowAddNewBranchModal(true);
  };

  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage);

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        {successMessage && <Alert variant="success">{successMessage}</Alert>}
        {loading && <LinearProgress />}
        <div className={classes.tableWrapper}>
          <Table className={classes.table} aria-labelledby="tableTitle">
            <StudentFamilyBranchTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {stableSort(rows, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => {
                  return (
                    <StudentFamilyBranchRow
                      key={row.user_id}
                      row={row}
                      onAddLocationClick={() => handleAddLocationClick(row)}
                    />
                  );
                })}
              {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Paper>
      <AddNewBranchModal
        show={showAddNewBranchModal}
        onHide={() => setShowAddNewBranchModal(false)}
        student={selectedStudent}
        onSuccess={handleBranchAdded}
      />
    </div>
  );
}
